
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { i18nManager } from "../../../../../../i18n";
import LoadingGif from "../../../../../../assets/images/loading_circle.gif";
import { BaseService, IGallery, IMember, ITournamentEdition, ITournamentEditionWebsite, SanitizedResponse, TournamentService } from "../../../../../../services";
import { Alignment, NoseurObject, TabPane, TabPanel, useScreenDimension } from "@rivtn/noseur";
import { Schemes, Utils } from "../../../../../../utils";
import Design from "./Design";
import MobileDesign from "./MobileDesign";
import SocialNetworks from "./general/SocialNetworks";
import BannerMedias from "./general/BannerMedias";
import WebsitePages from "./general/WebsitePages";
import AppPages from "./general/AppPages";
import ImportExport from "../../../../../page_design/ImportExport";
import TourSettings from "./general/TourSettings";
import Home from "./Home";
import Events from "./Events";
import Merch from "../../../../../shared/settings/Merch";
import { Siren } from "../../../../../shared/ConfirmDialog";
import Participants from "./Participants";
import Schedule from "./Schedule";
import Gallery from "../../../../../shared/settings/Gallery";
import Visibilities from "./about/Visibilities";
import Rules from "./about/Rules";
import Faqs from "./about/Faqs";
import Details from "./about/Details";
import Trophies from "./Trophies";
import Leaderboard from "./Leaderboard";

interface TournamentSettingsProps {
    profile: IMember;
    tournamentEdition: ITournamentEdition;
}

function TournamentSettings(props: TournamentSettingsProps) {

    const { tournamentEdition } = props;
    const { width } = useScreenDimension();
    const merchRivtnAdStateState = React.useState<boolean>();
    const tournamentService = TournamentService.getInstance();
    const galleryRivtnAdStateState = React.useState<boolean>();
    const [profile, _] = React.useState<IMember>(props.profile);
    const aboutTabs = ["VISIBILITIES", "RULES", "FAQS", "DETAILS"];
    const labels = i18nManager.Labels.host.manage_tournament.settings;
    const tournamentEditionWebsiteGalleryState = React.useState<IGallery[]>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const genTabs = ["TNETS", "TMEDIAS", "WEBSITE_PAGES", "MOBILE_APPS_FEATURES", "TSETTINGS", "IMEXPORT"];
    const [tournamentEditionWebsite, setTournamentEditionWebsite] = React.useState<ITournamentEditionWebsite>();
    const tabs = ["GENERAL", "HOME", "EVENTS", "MERCH", "PARTICIPANTS", "SCHEDULE", "TROPHIES", "LEADERBOARD", "GALLERY", "ABOUT", "WEBSITE", "MOBILE_APP"];

    React.useEffect(() => {
        fetchInformation();
    }, []);

    return (<div className="settings earnings analytics" style={{ height: "100%", padding: "0px" }}>
        {(!tournamentEdition || !tournamentEditionWebsite)
            ? (<div style={{ margin: "auto" }}>
                <img alt={"loading"} src={LoadingGif} style={{ width: 80, margin: "auto" }} />
            </div>)
            : (<TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} activeIndex={tabs.indexOf(searchParams.get("tab") ?? "GENERAL")}
                style={{ marginTop: 10, marginLeft: 10, height: "100%" }} attrsRelay={{ content: { style: { margin: 0, padding: 0, border: "none" } } }} onTabActive={(i) => {
                    Utils.updateSearchParams("tab", tabs[i], setSearchParams);
                }}>
                <TabPanel header={labels.general.title} >
                    <div className="te-settings-gen">
                        <TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} alignment={width > 800 ? Alignment.LEFT : Alignment.TOP}
                            activeIndex={genTabs.indexOf(searchParams.get("gtab") ?? "TNETS")} style={{ height: "100%", maxWidth: (width > 800 ? "100%" : "90vw") }}
                            attrsRelay={{ navigator: { headers: { style: { borderRight: "none", marginRight: 20, paddingLeft: (width > 800 ? 0 : 450) } } } }} onTabActive={(i) => {
                                Utils.updateSearchParams("gtab", genTabs[i], setSearchParams);
                            }}>
                            <TabPanel header={labels.general.social_networks.title} attrsRelay={{ header: { style: { marginTop: (width > 800 ? 30 : 20) } } }}>
                                <SocialNetworks tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.banner_medias.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <BannerMedias tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.website_pages.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <WebsitePages tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.mobile_app_feature.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <AppPages tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.tour_settings.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <TourSettings tournamentEdition={tournamentEdition} profile={profile} />
                            </TabPanel>
                            <TabPanel header={i18nManager.Labels.import_export.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <ImportExport section="tournament_edition_website" export={() => new Promise((resolve, _) => {
                                    setTimeout(() => {
                                        resolve(i18nManager.Labels.import_export.cannot_import_settings);
                                    }, 2000)
                                })} import={async (settings: NoseurObject<string>) => new Promise((resolve, _) => {
                                    setTimeout(() => {
                                        resolve(i18nManager.Labels.import_export.cannot_import_settings);
                                    }, 2000)
                                })} />
                            </TabPanel>
                        </TabPane>
                    </div>
                </TabPanel>

                <TabPanel header={labels.home.title} >
                    <Home tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.events.title} >
                    <Events tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.merch.title} >
                    <Merch noCompound profile={profile} enableRivtnAdState={merchRivtnAdStateState} updateSettings={(enableRivtnAd: boolean) => {
                        merchRivtnAdStateState[1](enableRivtnAd);
                        tournamentService.updateTournamentEditionWebsite(tournamentEdition.external_id, { ...tournamentEditionWebsite, merchandise_settings: { ...tournamentEditionWebsite.merchandise_settings, enable_rivtn_ad: enableRivtnAd } }).then(({ }: SanitizedResponse<ITournamentEditionWebsite>) => {
                            Siren.alert(labels.merch.update_successful);
                        }).catch(BaseService.reportError);
                    }} />
                </TabPanel>

                <TabPanel header={labels.participants.title} >
                    <Participants tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.schedule.title} >
                    <Schedule tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.trophies.title} >
                    <Trophies tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.leaderboard.title} >
                    <Leaderboard tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.gallery.title} >
                    <Gallery noCompound profile={profile} galleryState={tournamentEditionWebsiteGalleryState}
                        deleteLabel={i18nManager.Labels.account.profile_website.gallery.dialog.delete.title}
                        previewActionKey="action_label" previewLocationKey="action_location" previewCaptionKey="caption"
                        updateGalleryMethod={(gallery) => tournamentService.updateGallery.bind(tournamentService)(tournamentEdition.external_id, gallery)} uploadPath="/omnehost/tour/edi/gallery/"
                        enableRivtnAdState={galleryRivtnAdStateState} addGalleryMethod={(gallery) => tournamentService.createGallery.bind(tournamentService)(tournamentEdition.external_id, gallery)}
                        deleteGalleryMethod={(gallery) => tournamentService.deleteGallery.bind(tournamentService)(tournamentEdition.external_id, gallery)} enableAdLabel={i18nManager.Labels.account.profile_website.gallery.enable_ad}
                        createLabel={i18nManager.Labels.account.profile_website.gallery.dialog.add_update.add_media_to_gallery} updateLabel={i18nManager.Labels.account.profile_website.gallery.dialog.add_update.update_media_in_gallery}
                        formControls={[
                            {
                                type: "file",
                                name: "media_location",
                            },
                            [
                                {
                                    type: "textbox",
                                    name: "action_label",
                                },
                                {
                                    type: "textbox",
                                    name: "action_location",
                                }
                            ],
                            {
                                name: "caption",
                                type: "textarea",
                            },
                        ]}
                        onUnMount={() => {

                        }} fetchGallery={() => {
                            tournamentService.getGallery(tournamentEdition.external_id).then(({ apiResponse }: SanitizedResponse<IGallery>) => {
                                tournamentEditionWebsiteGalleryState[1](apiResponse.data?.content);
                            }).catch(BaseService.reportError);
                        }} updateSettings={(enableRivtnAd: boolean) => {
                            galleryRivtnAdStateState[1](enableRivtnAd);
                            tournamentService.updateTournamentEditionWebsite(tournamentEdition.external_id, { ...tournamentEditionWebsite, gallery_settings: { ...tournamentEditionWebsite.gallery_settings, enable_rivtn_ad: enableRivtnAd } }).then(({ }: SanitizedResponse<ITournamentEditionWebsite>) => {
                                Siren.alert(labels.gallery.update_successful);
                            }).catch(BaseService.reportError);
                        }} />
                </TabPanel>

                <TabPanel header={labels.about.title} >
                    <div className="te-settings-gen">
                        <TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} alignment={width > 800 ? Alignment.LEFT : Alignment.TOP}
                            activeIndex={aboutTabs.indexOf(searchParams.get("atab") ?? "VISIBILITIES")} style={{ height: "100%" }}
                            attrsRelay={{ navigator: { headers: { style: { borderRight: "none", marginRight: 20, justifyContent: "flex-start" } }, style: { width: (width > 800 ? undefined : width - 50) } } }} onTabActive={(i) => {
                                Utils.updateSearchParams("atab", aboutTabs[i], setSearchParams);
                            }}>
                            <TabPanel header={labels.about.visibilities.title} attrsRelay={{ header: { style: { marginTop: (width > 800 ? 30 : 20) } } }}>
                                <Visibilities tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.about.rules.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <Rules tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.about.faqs.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <Faqs tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.about.details.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <Details tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                            </TabPanel>
                        </TabPane>
                    </div>
                </TabPanel>

                <TabPanel header={labels.website.title} >
                    <Design tournamentEdition={tournamentEdition} tournamentEditionWebsite={tournamentEditionWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.mobile_app.title} >
                    <MobileDesign tournamentEdition={tournamentEdition} profile={profile} />
                </TabPanel>
            </TabPane>)}
    </div>);

    function fetchInformation() {
        tournamentService.getTournamentEditionWebsite(tournamentEdition.external_id).then(({ sanitized }: SanitizedResponse<ITournamentEditionWebsite>) => {
            setTournamentEditionWebsite(sanitized);
            galleryRivtnAdStateState[1](sanitized.gallery_settings.enable_rivtn_ad);
            merchRivtnAdStateState[1](sanitized.merchandise_settings.enable_rivtn_ad);
        }).catch(BaseService.reportError);
    }

}

export default TournamentSettings;
